/**
 * Shared occupancy guard — one reading of `pets_allowed` and `maximum_guest_count`
 * for every widget that lets a visitor express a party size.
 *
 * These are property CONSTRAINTS, but until now each widget decided on its own what
 * they meant. The explorer pushed them to Central as feed filters (`pets`, and
 * `adults` + `children` against capacity) and never saw the underlying values. The
 * booking picker re-derived them inline from its own property payload. Two readings
 * of one constraint drift, and the drift was only ever visible to a visitor:
 *
 *   - A "Pets: Yes" search handed off to a property page and the link dropped the
 *     pets flag entirely (PropertyCard forwarded dates, adults and children, but
 *     never pets), so the picker came up at 0 pets and the pet silently left the
 *     quote and the reservation.
 *   - A URL-seeded `guests=12` sailed past a max-5 property, because the stepper's
 *     `max` only blocks the NEXT increment — it never re-examines a seeded value.
 *     Reachable from an edited URL, a shared link, a stale bookmark, or any page
 *     whose booking widget points at a different property than the card that
 *     produced the link.
 *
 * What those fields mean, how a party travels between widgets, and how a requested
 * party is clamped to them all live here now. Both ends call the same functions, so
 * a constraint is enforced when the visitor first asks for it and again before
 * anything reaches Central.
 *
 * Deliberately dependency-free (no React, no zod) so the SSR renderer, the widgets,
 * and plain tests can all use it.
 */

/** Infants and pets don't count toward the property guest maximum, so they need their own bounds. */
export const PETS_MAX = 5
export const INFANTS_MAX = 5

/**
 * Guest ceiling used while the property is still unresolved. Generous on purpose:
 * the picker should never look artificially restrictive during a load. It is a
 * DISPLAY bound only — `clampParty` re-clamps against the real maximum the moment
 * the property settles, and nothing is submitted before then.
 */
export const GUEST_MAX_FALLBACK = 20

/** The fields any occupancy decision reads. Everything else on the payload is ignored. */
export type OccupancyProperty = {
  pets_allowed?: number | null
  maximum_guest_count?: number | null
  /**
   * Per-property pets ceiling (hudsoncreativestudio/homerunner-central#3195).
   * A positive integer overrides PETS_MAX; absent, null, zero, or a non-integer
   * means "no per-property count known" and keeps the PETS_MAX fallback —
   * matching Central's own `maxPetsOf()` collapse.
   */
  max_pets?: number | null
} | null | undefined

export type OccupancyGuard = {
  /**
   * The property loaded, or its fetch gave up. Until this is true we can't rule out
   * a no-pets property, so pets stay OUT of anything submitted.
   */
  settled: boolean
  /** Only an affirmative `pets_allowed = 0` denies pets; a payload missing the field stays permissive. */
  petsAllowed: boolean
  /**
   * Lock the pets control. Narrower than `!petsAllowed` on purpose: it requires a
   * LOADED property, so there's no false "Not allowed" flash while the fetch is in
   * flight and no permanent lock if the property never loads.
   */
  petsLocked: boolean
  maxGuests: number
  maxPets: number
  /**
   * True when `maxPets` came from a REAL configured ceiling — the property's
   * own `max_pets` or the feed-level pets-cap fallback (#1407) — false when
   * it is only the platform's generic PETS_MAX constant. Toolkit parity: the
   * at-cap note/tooltip only ever fires against a real number (GuestPicker.php
   * `$max_pets` resolves to 0 = "no cap" unless a per-property or
   * account-wide setting is present, and guest-picker.js never shows the
   * tooltip at 0).
   */
  maxPetsConfigured: boolean
}

/**
 * Read a property payload into the guard.
 *
 * `failed` marks a property fetch that errored: it settles permissive, because
 * there's nothing left to verify against and silently dropping the visitor's pets
 * would be worse than sending them.
 *
 * `feedMaxPets` is the widget's own feed-level pets-cap fallback setting
 * (#1407, follow-up to #1230/#1405; toolkit parity: guest-picker's
 * account-wide `max_pets` admin setting, GuestPicker.php:88-100). It is used
 * only when the property carries no per-property `max_pets` of its own, and
 * — like the toolkit's account-wide setting — is available independently of
 * whether the property has loaded yet.
 */
export function resolveOccupancyGuard(
  property: OccupancyProperty,
  opts: { failed?: boolean; feedMaxPets?: number | null } = {},
): OccupancyGuard {
  const loaded = !!property
  const petsAllowed = property?.pets_allowed !== 0
  const perPropertyMaxPets = property?.max_pets
  const hasPerPropertyMaxPets = Number.isInteger(perPropertyMaxPets) && (perPropertyMaxPets as number) > 0
  const feedMaxPets = opts.feedMaxPets
  const hasFeedMaxPets = Number.isInteger(feedMaxPets) && (feedMaxPets as number) > 0
  const maxPetsConfigured = hasPerPropertyMaxPets || hasFeedMaxPets
  const maxPets = hasPerPropertyMaxPets
    ? (perPropertyMaxPets as number)
    : hasFeedMaxPets
      ? (feedMaxPets as number)
      : PETS_MAX
  return {
    settled: loaded || !!opts.failed,
    petsAllowed,
    petsLocked: loaded && !petsAllowed,
    maxGuests: property?.maximum_guest_count || GUEST_MAX_FALLBACK,
    maxPets,
    maxPetsConfigured,
  }
}

/**
 * The pets count that may actually be submitted. Derived, never held in state, so a
 * denial can't lose a race with an in-flight quote: a property switch blanks the
 * payload (the property query keeps no placeholder), which un-settles the guard and
 * drops pets until the new property answers for itself.
 */
export function guardPets(requested: number, guard: OccupancyGuard): number {
  if (!guard.settled || !guard.petsAllowed) return 0
  if (!Number.isInteger(requested) || requested < 0) return 0
  return Math.min(requested, guard.maxPets)
}

/**
 * Fit a requested party inside the property's guest maximum. Adults win the room:
 * children give way first, and at least one adult always survives, so a clamp can
 * never produce an unbookable party of zero.
 */
export function clampParty(
  party: { adults: number; children?: number },
  guard: Pick<OccupancyGuard, 'maxGuests'>,
): { adults: number; children: number } {
  const ceiling = Math.max(1, guard.maxGuests)
  const adults = Math.min(Math.max(1, Math.floor(party.adults) || 1), ceiling)
  const requestedChildren = Math.max(0, Math.floor(party.children || 0))
  return { adults, children: Math.min(requestedChildren, ceiling - adults) }
}

/** Fit a single undifferentiated guest total inside the property's maximum. */
export function clampGuestTotal(total: number, guard: Pick<OccupancyGuard, 'maxGuests'>): number {
  const ceiling = Math.max(1, guard.maxGuests)
  return Math.min(Math.max(1, Math.floor(total) || 1), ceiling)
}

// --- Transport between widgets -----------------------------------------------------
// The explorer writes these onto the property link; the booking widget reads them back.
// Both sides go through the helpers below so a rename can't desync one end from the
// other — that desync is exactly how `pets` got lost.

export const OCCUPANCY_PARAMS = ['adults', 'children', 'guests', 'pets'] as const

export type PartyParams = {
  adults?: number
  children?: number
  guests?: number
  pets?: number
}

/**
 * Party counts arrive from a URL anyone can edit. Only finite non-negative whole
 * numbers are honored; negative, fractional, or unparseable values are ignored
 * rather than fed into the guest math and the quote body.
 */
function countParam(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key)
  if (!raw) return undefined
  const n = Number(raw)
  if (!Number.isInteger(n) || n < 0) return undefined
  return n
}

/**
 * Read a party off a URL.
 *
 * `pets` spans two vocabularies: the explorer filter is a boolean ("show me
 * pet-friendly places") and the booking picker is a count. `true`/`1` both mean one
 * pet, and an explicit larger count rides through, so either producer round-trips.
 */
export function readPartyParams(search: string | URLSearchParams): PartyParams {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search
  const rawPets = params.get('pets')
  const pets = rawPets === 'true' ? 1 : countParam(params, 'pets')
  return {
    adults: countParam(params, 'adults'),
    children: countParam(params, 'children'),
    guests: countParam(params, 'guests'),
    pets,
  }
}

/**
 * Write a party onto a property link. `guests` carries the adults + children SUM for
 * consumers that don't split the breakdown; `adults` stays the adults-only count.
 * Zeroes are omitted rather than written, so a link never pins a default.
 *
 * `pets` is deliberately narrower than `readPartyParams`'s reader: the explorer's
 * pet-friendly filter is a BOOLEAN ("show me pet-friendly places") and is not
 * itself a party member — carrying it onto the property link used to seed a
 * phantom pet in the booking quote, pre-applying a per-pet fee nobody asked for
 * (#850 product call 2). Only an explicit NUMERIC pets count (the booking
 * picker's own vocabulary) travels here; a boolean writes nothing.
 */
export function writePartyParams(
  params: URLSearchParams,
  party: { adults?: number; children?: number; pets?: boolean | number },
): URLSearchParams {
  if (party.adults) params.set('adults', String(party.adults))
  if (party.children) params.set('children', String(party.children))
  const guests = (party.adults ?? 0) + (party.children ?? 0)
  if (guests > 0) params.set('guests', String(guests))
  const pets = typeof party.pets === 'number' ? party.pets : 0
  if (pets > 0) params.set('pets', String(pets))
  return params
}
